/**
 * Self-hosted project assets.
 *
 * Project CSS is mirrored from the ImmutablesArtImages repo into
 * public/project-css/{networkId}/{projectId}/style[.mobile].css, keeping that
 * repo's directory layout. Serving it from our own origin means page styling no
 * longer depends on an IPFS gateway being reachable, and it is the only way
 * archive mode gets styled at all — ArchiveProvider has no chain access to
 * resolve a project's IPFS CID.
 *
 * Projects without a mirrored stylesheet simply 404 here and the caller falls
 * back to IPFS (blockchain mode) or to no custom styling (archive mode).
 */

const ASSET_BASE = `${process.env.PUBLIC_URL}/project-css/`;

/**
 * @param {number|string} networkId
 * @param {number|string} projectId
 * @param {boolean} isMobile
 * @returns {string|null}
 */
export function projectCSSUrl(networkId, projectId, isMobile) {
  if (!networkId || !projectId) return null;
  const file = isMobile ? 'style.mobile.css' : 'style.css';
  return `${ASSET_BASE}${networkId}/${projectId}/${file}`;
}

/**
 * Fetch the self-hosted stylesheet for a project.
 *
 * @returns {Promise<string|null>} CSS text, or null when none is mirrored.
 */
export async function fetchSelfHostedProjectCSS(networkId, projectId, isMobile, timeout = 4000) {
  const url = projectCSSUrl(networkId, projectId, isMobile);
  if (!url) return null;

  // Same reason as the archive cache: the single-file build inlines these,
  // because a file:// origin cannot fetch them.
  const embedded = typeof window !== 'undefined' && window.__PROJECT_CSS;
  if (embedded) {
    const key = `${networkId}/${projectId}/${isMobile ? 'style.mobile.css' : 'style.css'}`;
    return Object.prototype.hasOwnProperty.call(embedded, key) ? embedded[key] : null;
  }

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);

    if (!response.ok) return null;

    const text = await response.text();
    if (!text || !text.trim()) return null;

    // Guard against a host that answers unknown paths with the SPA shell
    // instead of a 404 — injecting an HTML document into a <style> tag would
    // silently break the page styling.
    if (text.trim().startsWith('<')) return null;

    return text;
  } catch (error) {
    return null;
  }
}
